import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, TrendingUp, Shield, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO_INDUSTRIES = [
  { value: 'car_dealership', label: 'Car Dealership', icon: '🚗' },
  { value: 'real_estate', label: 'Real Estate', icon: '🏠' },
  { value: 'legal_firm', label: 'Legal Firm', icon: '⚖️' },
  { value: 'it_support', label: 'IT Support / MSP', icon: '🖥️' },
  { value: 'customer_support', label: 'Customer Support', icon: '🎧' },
  { value: 'service_department', label: 'Service Department', icon: '🔧' },
  { value: 'dental_clinic', label: 'Dental Clinic', icon: '🦷' },
  { value: 'finance_mortgage', label: 'Finance & Mortgage', icon: '🏦' },
  { value: 'insurance_broker', label: 'Insurance Broker', icon: '🛡️' },
  { value: 'construction', label: 'Construction', icon: '🏗️' },
  { value: 'education_training', label: 'Education & Training', icon: '🧑‍🏫' },
  { value: 'recruitment', label: 'Recruitment', icon: '💼' },
  { value: 'property_management', label: 'Property Management', icon: '🏢' },
  { value: 'b2b_sales', label: 'B2B Sales', icon: '📊' },
  { value: 'fitness_gym', label: 'Fitness & Gyms', icon: '🏋️' },
  { value: 'emergency_services', label: 'Emergency Services', icon: '🧯' }
];

const DemoLoginPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      try {
        const response = await axios.get(`${API}/knowledge-bases`);
        setKnowledgeBases(response.data);
      } catch {
        console.error('Failed to load knowledge bases');
      }
    };
    fetchKnowledgeBases();
  }, []);

  const handleDemoLogin = async () => {
    if (!selectedIndustry) {
      toast.error('Please select your industry to continue');
      return;
    }
    
    setLoading(true);
    
    const industryLabel = DEMO_INDUSTRIES.find(i => i.value === selectedIndustry)?.label || 'Demo User';
    const demoEmail = `demo.${selectedIndustry}@yabbit.com`;
    
    try {
      await register(demoEmail, 'demo123', `${industryLabel} Demo`, 'manager');
      toast.success(`Welcome to ${industryLabel} coaching!`);
    } catch {
      try {
        await login(demoEmail, 'demo123');
        toast.success(`Welcome back to ${industryLabel} coaching!`);
      } catch {
        toast.error('Demo login failed. Please try again.');
        setLoading(false);
        return;
      }
    }
    
    setLoading(false);
    navigate('/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      if (userData?.role === 'master_admin') navigate('/select-mode');
      else if (userData?.domain_id) navigate('/my-domain');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    
    setLoading(false);
  };

  if (showDemo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4">
        <div className="w-full max-w-5xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png" 
                alt="Yabbit Logo" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
              Yabbit VoiceFabric
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Transform phone conversations into performance insights, coaching intelligence, and revenue growth
            </p>
          </div>

          <Card className="shadow-2xl border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-white to-slate-50">
              <CardTitle className="text-2xl">Choose Your Industry</CardTitle>
              <CardDescription>Select your business type to see industry-specific coaching criteria</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {DEMO_INDUSTRIES.map((industry) => (
                  <button
                    key={industry.value}
                    onClick={() => setSelectedIndustry(industry.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-left hover:shadow-md ${
                      selectedIndustry === industry.value
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-slate-200 hover:border-primary/50'
                    }`}
                    data-testid={`industry-${industry.value}`}
                  >
                    <div className="text-3xl mb-2">{industry.icon}</div>
                    <div className="text-sm font-medium text-slate-900">{industry.label}</div>
                  </button>
                ))}
              </div>

              {selectedIndustry && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg animate-slide-in">
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">
                        {DEMO_INDUSTRIES.find(i => i.value === selectedIndustry)?.label} Coaching Enabled
                      </h4>
                      <p className="text-sm text-blue-800">
                        {knowledgeBases.find(kb => kb.call_type === selectedIndustry)?.description || 
                         'AI-powered coaching with industry-specific criteria'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={handleDemoLogin}
                disabled={!selectedIndustry || loading}
                className="w-full h-12 text-lg"
                data-testid="demo-login-btn"
              >
                {loading ? 'Loading Demo...' : 'Start Demo →'}
              </Button>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <button
                  onClick={() => setShowDemo(false)}
                  className="text-sm text-slate-600 hover:text-primary transition-colors"
                >
                  Already have an account? Sign in here
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Value Propositions */}
          <div className="grid md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <TrendingUp className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm mb-1">Revenue Intelligence</h3>
              <p className="text-xs text-slate-600">Track performance & conversion metrics</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <Phone className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm mb-1">Behavioral Coaching</h3>
              <p className="text-xs text-slate-600">AI-powered feedback & improvement plans</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <Shield className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm mb-1">Risk Detection</h3>
              <p className="text-xs text-slate-600">Compliance & governance monitoring</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <Zap className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm mb-1">Auto QA</h3>
              <p className="text-xs text-slate-600">Automated quality assurance scoring</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png" 
              alt="Yabbit Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            Sign In
          </h1>
          <p className="text-slate-600">Access your coaching dashboard</p>
        </div>

        <Card className="shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle>Account Login</CardTitle>
            <CardDescription>Enter your credentials to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <button
                onClick={() => setShowDemo(true)}
                className="text-sm text-slate-600 hover:text-primary transition-colors"
              >
                ← Back to demo selection
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemoLoginPage;
